/**
 * main.ts
 *
 * Bootstraps Vuetify and other plugins then mounts the App`
 */

// Plugins
import { registerPlugins } from '@/plugins'
import router from './router'
import * as Sentry from '@sentry/vue'
import { useSnackbarStore } from './store/snackbar'
import { registerLicense } from '@syncfusion/ej2-base'
registerLicense(import.meta.env.VITE_SYNC_FUSION_KEY)

// Components
import App from './App.vue'

// Composables
import { createApp } from 'vue'
import { APP_BUILD_STORAGE_KEY, clearAuthSession } from '@/utils/session'
import { initPostHog } from '@/utils/posthog'

const app = createApp(App)

Sentry.init({
  app,
  dsn: 'https://26ee44e61c86a0523f7be1bc26b0e854@o4507710674436096.ingest.us.sentry.io/4507992730370048',
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration()
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ['localhost', /^https:\/\/yourserver\.io\/api/],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0 // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
})

registerPlugins(app)

const snackbarStore = useSnackbarStore()
let isRefreshingForNewVersion = false
let versionCheckTimer: number | null = null

const syncSessionWithCurrentBuild = () => {
  const previousBuildId = localStorage.getItem(APP_BUILD_STORAGE_KEY)

  if (previousBuildId && previousBuildId !== __APP_BUILD_ID__) {
    clearAuthSession()
  }

  localStorage.setItem(APP_BUILD_STORAGE_KEY, __APP_BUILD_ID__)
}

const reloadForNewVersion = () => {
  if (isRefreshingForNewVersion) return
  isRefreshingForNewVersion = true

  clearAuthSession()
  localStorage.setItem(APP_BUILD_STORAGE_KEY, __APP_BUILD_ID__)

  snackbarStore.showSnackbar({
    text: 'Nova versão disponível. Atualizando...',
    color: 'primary',
    timeout: 2000
  })

  window.setTimeout(() => {
    window.location.reload()
  }, 1500)
}

const checkAppVersion = async () => {
  try {
    const response = await fetch(`/version.json?_ts=${Date.now()}`, {
      cache: 'no-store'
    })
    if (!response.ok) return

    const payload = (await response.json()) as { buildId?: string }
    const latestBuildId = String(payload?.buildId || '')

    if (latestBuildId && latestBuildId !== __APP_BUILD_ID__) {
      reloadForNewVersion()
    }
  } catch (_error) {
    // ignore version check failures
  }
}

const startVersionPolling = () => {
  void checkAppVersion()

  if (!versionCheckTimer) {
    versionCheckTimer = window.setInterval(() => {
      void checkAppVersion()
    }, 60000)
  }

  window.addEventListener('focus', () => {
    void checkAppVersion()
  })

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      void checkAppVersion()
    }
  })
}

syncSessionWithCurrentBuild()
startVersionPolling()
initPostHog()

app.mount('#app')
